import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Link,
  Grid,
  Divider,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { buildReviewModel } from "./listNewAssetDraft";
import { iconForAmenityLabel } from "./listNewAssetCreationData";
import AssetOwnerImage from "../AssetOwnerImage";

/** Non–real-estate categories that skip address / SPV / amenities in the review UI */
function isCommodityOrLuxuryCategory(title) {
  const t = String(title || "").trim().toLowerCase();
  return t === "commodities" || t === "luxury goods";
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
      <Icon sx={{ fontSize: 22, color: "text.muted" }} />
      <Typography sx={{ fontWeight: 700, fontSize: 17, color: "text.primary" }}>
        {title}
      </Typography>
    </Stack>
  );
}

function FieldRow({ label, value, valueSx = {} }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.muted", mb: 0.35 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary", ...valueSx }}>
        {value}
      </Typography>
    </Box>
  );
}

function RowField({ label, value, valueSx = {} }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1.25 }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.muted" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", ...valueSx }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function ListNewAssetStep7({ draft,
  creationData,
  prevStep,
  onSubmit,
  isSubmitting,
  wizardStep = 8,
  wizardTotalSteps = 8,
  categoryChipLabel, }) {
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;

  const review = useMemo(
    () =>
      buildReviewModel(draft, {
        spvStructureTypeOptions: creationData?.spvStructureTypeOptions,
      }),
    [draft, creationData?.spvStructureTypeOptions]
  );

  const categoryTitle = draft.categoryTitle || categoryChipLabel || "";
  const hideAddressPropertyAndSpvSections = isCommodityOrLuxuryCategory(categoryTitle);

  return (
    <>
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1100,
          mx: "auto",
          minHeight: "100vh",
        }}
      >

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={prevStep}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: TEAL,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: "none",
              p: 0,
              "&:hover": { color: TEAL_HOVER },
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
            Back
          </Link>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={categoryChipLabel || review.category}
              size="small"
              sx={{
                bgcolor: (theme) => theme.palette.brand.cyanSoft,
                color: (theme) => theme.palette.brand.cyanDark,
                fontWeight: 600,
                fontSize: 12,
                height: 26,
              }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
              Step {wizardStep} of {wizardTotalSteps}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
          {Array.from({ length: wizardTotalSteps }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 999,
                bgcolor: i < wizardStep ? TEAL : theme.palette.border.light,
              }}
            />
          ))}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: "14px",
            bgcolor: "background.paper",
            boxShadow: (theme) => theme.palette.shadow.cardStrong,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}>
            Review Your Asset
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
            Please review all information before submitting.
          </Typography>

          {/* Hero */}
          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              mb: 2,
              minHeight: { xs: 200, sm: 280 },
            }}
          >
            <AssetOwnerImage
              src={review.heroImage}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", minHeight: 280 }}
            />
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                p: 2.5,
                background: "linear-gradient(transparent, rgba(0,0,0,0.75))",
              }}
            >
              <Typography sx={{ color: "common.white", fontWeight: 700, fontSize: 24, mb: 1 }}>
                {review.assetName}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                <Chip
                  label={review.category}
                  size="small"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.common.white, 0.25),
                    color: "common.white",
                    fontWeight: 600,
                  }}
                />
                {review.location && review.location !== "—" ? (
                <Chip
                  label={review.location}
                  size="small"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.common.white, 0.25),
                    color: "common.white",
                    fontWeight: 600,
                  }}
                />
                ) : null}
              </Stack>
            </Box>
          </Box>

          {/* Gallery */}
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "text.primary", mb: 1.5 }}>
            Gallery Images
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 4, overflowX: "auto", pb: 1 }}>
            {review.galleryThumbs.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: "text.muted" }}>No gallery images</Typography>
            ) : (
              review.galleryThumbs.map((src, idx) => (
                <AssetOwnerImage
                  key={idx}
                  src={src}
                  alt=""
                  sx={{
                    width: 96,
                    height: 72,
                    borderRadius: 1,
                    objectFit: "cover",
                    flexShrink: 0,
                    border: (theme) => `1px solid ${theme.palette.border.light}`,
                  }}
                />
              ))
            )}
          </Stack>

          {/* Asset Details */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2.5,
              borderRadius: "14px",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              bgcolor: "background.paper",
            }}
          >
            <SectionHeader icon={ApartmentOutlinedIcon} title="Asset Details" />
            <FieldRow label="Description" value={review.description} />
            {!hideAddressPropertyAndSpvSections ? (
              <>
                <Divider sx={{ my: 1.5, borderColor: (t) => t.palette.border.light }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldRow label="Street Address" value={review.streetAddress} />
                  </Grid>
                  {review.location && review.location !== "—" ? (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldRow label="Location" value={review.location} />
                    </Grid>
                  ) : null}
                </Grid>
                <Divider sx={{ my: 1.5, borderColor: (t) => t.palette.border.light }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldRow label="Property Type" value={review.propertyType} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FieldRow label="Size" value={review.size} />
                  </Grid>
                </Grid>
              </>
            ) : null}
          </Paper>

          {/* Features — Real Estate only */}
          {!hideAddressPropertyAndSpvSections ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: "14px",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                bgcolor: "background.paper",
              }}
            >
              <SectionHeader icon={StarBorderOutlinedIcon} title="Features & Amenities" />
              {review.featureList.length === 0 ? (
                <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                  No features selected
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {review.featureList.map((label) => {
                    const Icon = iconForAmenityLabel(label);
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={label}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Icon
                            sx={{
                              fontSize: 22,
                              color: (theme) => theme.palette.brand.cyan,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}
                          >
                            {label}
                          </Typography>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>
          ) : null}

          {/* SPV — Real Estate flow only */}
          {!hideAddressPropertyAndSpvSections ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: "14px",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                bgcolor: "background.paper",
              }}
            >
              <SectionHeader icon={ShieldOutlinedIcon} title="SPV Structure" />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="SPV Name" value={review.spv.name} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="Registration Number" value={review.spv.registrationNumber} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="Jurisdiction" value={review.spv.jurisdiction} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="License Type" value={review.spv.licenseType} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="Formation Date" value={review.spv.formationDate} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="Purpose" value={review.spv.purpose} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldRow label="Authorized Signatory" value={review.spv.signatoryFile} />
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {/* Financial + Token */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: "100%",
                  borderRadius: "14px",
                  border: (theme) => `1px solid ${theme.palette.border.light}`,
                  bgcolor: "background.paper",
                }}
              >
                <SectionHeader icon={ShowChartOutlinedIcon} title="Financial Details" />
                <RowField label="Total Valuation" value={review.financial.totalValuation} />
                <RowField
                  label="Expected ROI"
                  value={review.financial.expectedRoi}
                  valueSx={{ color: (theme) => theme.palette.success.main }}
                />
                <RowField label="Rental Yield" value={review.financial.rentalYield} />
                <RowField label="Minimum Investment" value={review.financial.minInvestment} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: "100%",
                  borderRadius: "14px",
                  border: (theme) => `1px solid ${theme.palette.border.light}`,
                  bgcolor: "background.paper",
                }}
              >
                <SectionHeader icon={TokenOutlinedIcon} title="Token Economics" />
                <RowField label="Total Supply" value={review.token.totalSupply} />
                <RowField label="Price Per Token" value={review.token.pricePerToken} />
                <RowField label="Blockchain" value={review.token.blockchain} />
                <RowField label="Fractional Ownership" value={review.token.fractional} />
              </Paper>
            </Grid>
          </Grid>

          {/* Documentation */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2.5,
              borderRadius: "14px",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              bgcolor: "background.subtle",
            }}
          >
            <SectionHeader icon={DescriptionOutlinedIcon} title="Documentation Status" />
            <Typography sx={{ fontSize: 14, color: "text.muted", mb: 2, lineHeight: 1.5 }}>
              These are the documents and images you attached in the earlier steps. Preview any file
              before you submit.
            </Typography>
            <Grid container spacing={1.5}>
              {review.documents.length === 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                    No files attached yet. Go back to add images, certificates, and other documents.
                  </Typography>
                </Grid>
              )}
              {review.documents.map((d, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${d.label}-${d.file}-${idx}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: "12px",
                      border: (theme) => `1px solid ${theme.palette.border.light}`,
                      bgcolor: "background.paper",
                      height: "100%",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {d.previewUrl && d.fileType !== "pdf" ? (
                        <AssetOwnerImage
                          src={d.previewUrl}
                          alt=""
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            objectFit: "cover",
                            flexShrink: 0,
                            border: (theme) => `1px solid ${theme.palette.border.light}`,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            flexShrink: 0,
                            bgcolor: (theme) => theme.palette.action.hover,
                            display: "grid",
                            placeItems: "center",
                            border: (theme) => `1px solid ${theme.palette.border.light}`,
                          }}
                        >
                          {d.fileType === "pdf" ? (
                            <PictureAsPdfOutlinedIcon
                              sx={{ fontSize: 28, color: "error.main" }}
                            />
                          ) : d.fileType === "image" ? (
                            <ImageOutlinedIcon sx={{ fontSize: 28, color: "primary.main" }} />
                          ) : (
                            <InsertDriveFileOutlinedIcon
                              sx={{ fontSize: 28, color: "text.muted" }}
                            />
                          )}
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                          <CheckCircleIcon
                            sx={{
                              fontSize: 18,
                              color: (theme) => theme.palette.success.main,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "text.primary",
                            }}
                          >
                            {d.label}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            wordBreak: "break-word",
                            fontFamily: "monospace",
                            mb: d.hint || d.previewUrl ? 0.75 : 0,
                          }}
                        >
                          {d.file}
                        </Typography>
                        {d.hint ? (
                          <Typography sx={{ fontSize: 11, color: "text.muted", mb: d.previewUrl ? 0.75 : 0 }}>
                            {d.hint}
                          </Typography>
                        ) : null}
                        {d.previewUrl ? (
                          <Button
                            component="a"
                            href={d.previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: 12,
                              py: 0.25,
                            }}
                          >
                            Preview in new tab
                          </Button>
                        ) : null}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Info banner */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              bgcolor: (theme) => theme.palette.brand.cyanSoft,
              border: (theme) => `1px solid ${theme.palette.accent.info.border}`,
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: (theme) => theme.palette.accent.info.icon,
                flexShrink: 0,
                mt: 0.25,
              }}
            />
            <Typography
              sx={{
                fontSize: 14,
                color: (theme) => theme.palette.accent.info.text,
                lineHeight: 1.55,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                Ready to Submit?
              </Box>{" "}
              Your asset will be submitted for admin review. Our admin team will verify all
              documentation and get back to you within 24 hours.
            </Typography>
          </Paper>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 4, pt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={prevStep}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: (theme) => theme.palette.border.medium,
                color: "text.neutral",
                px: 3,
                py: 1,
                "&:hover": {
                  borderColor: (theme) => theme.palette.text.faint,
                  bgcolor: "background.subtle",
                },
              }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              endIcon={<SendRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={onSubmit}
              disabled={isSubmitting}
              sx={{
                px: 1.5,
                py: 1,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TEAL,
                borderRadius: "12px",
                boxShadow: "none",
                "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
                "&:disabled": {
                  bgcolor: (theme) => theme.palette.text.faint,
                  color: (theme) => theme.palette.background.paper,
                },
              }}
            >
              {isSubmitting ? "Submitting…" : "Submit Asset"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
